"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { publicEnv } from "@/config/env";
import type { ApiDropCurationRequest } from "@/generated/models/ApiDropCurationRequest";
import { commonApiDeleteWithBody } from "@/services/api/common-api";
import { getAuthJwt, getStagingAuth } from "@/services/auth/auth.utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { getDropCurationsQueryKey, type DropCurationMembership } from "./useDropCurations";
type DropCurationMembershipAction = "add" | "remove";
interface DropCurationMembershipMutationOptions {
  readonly suppressToast?: boolean | undefined;
}
interface DropCurationMembershipMutationVariables {
  readonly curationId: string;
  readonly action: DropCurationMembershipAction;
  readonly options?: DropCurationMembershipMutationOptions | undefined;
}
const getDropCurationMembershipEndpoint = (dropId: string): string => `${publicEnv.API_ENDPOINT}/api/drops/${dropId}/curations`;
const getDropCurationMembershipHeaders = (): Record<string, string> => {
  const apiAuth = getStagingAuth();
  const walletAuth = getAuthJwt();
  return {
    "Content-Type": "application/json",
    ...(apiAuth ? {
      "x-6529-auth": apiAuth
    } : {}),
    ...(walletAuth ? {
      Authorization: `Bearer ${walletAuth}`
    } : {})
  };
};
const getErrorMessageFromResponse = async (response: Response): Promise<string> => {
  const fallbackErrorMessage = response.statusText || "Something went wrong";
  try {
    const rawContent = await response.text();
    if (!rawContent) {
      return fallbackErrorMessage;
    }
    try {
      const parsedBody = JSON.parse(rawContent) as Record<string, unknown>;
      if (typeof parsedBody["error"] === "string") {
        return parsedBody["error"];
      }
      if (typeof parsedBody["message"] === "string") {
        return parsedBody["message"];
      }
    } catch {
      return rawContent;
    }
    return rawContent;
  } catch {
    return fallbackErrorMessage;
  }
};
export const addDropToCuration = async ({
  dropId,
  body
}: {
  readonly dropId: string;
  readonly body: ApiDropCurationRequest;
}): Promise<void> => {
  const response = await fetch(getDropCurationMembershipEndpoint(dropId), {
    method: "POST",
    headers: getDropCurationMembershipHeaders(),
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(await getErrorMessageFromResponse(response));
  }
};
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
};
const isPermissionErrorMessage = (message: string): boolean => {
  const normalizedMessage = message.toLowerCase();
  return normalizedMessage.includes("permission") || normalizedMessage.includes("forbidden") || normalizedMessage.includes("not authorized") || normalizedMessage.includes("not allowed") || normalizedMessage.includes("cannot curate") || normalizedMessage.includes("can't curate") || normalizedMessage.includes("not a member");
};
const getCurationMembershipErrorMessage = (error: unknown, action: DropCurationMembershipAction): string => {
  const fallbackErrorMessage = action === "add" ? "Failed to add drop to curation." : "Failed to remove drop from curation.";
  const errorMessage = getErrorMessage(error, fallbackErrorMessage);
  if (isPermissionErrorMessage(errorMessage)) {
    return action === "add" ? "You can't add this drop because you can't curate with the selected group." : "You can't remove this drop because you can't curate with the selected group.";
  }
  return errorMessage;
};
export function useDropCurationMembershipMutation(t0) {
  const $ = _c(29);
  const {
    dropId
  } = t0;
  const queryClient = useQueryClient();
  const {
    invalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const {
    setToast
  } = useAuth();
  let t1;
  if ($[0] !== dropId) {
    t1 = async t2 => {
      const {
        curationId,
        action
      } = t2;
      const body = {
        curation_id: curationId
      };
      if (action === "add") {
        await addDropToCuration({
          dropId,
          body
        });
        return;
      }
      await commonApiDeleteWithBody({
        endpoint: `drops/${dropId}/curations`,
        body
      });
    };
    $[0] = dropId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== dropId || $[3] !== queryClient) {
    t2 = async t3 => {
      const {
        curationId: curationId_0,
        action: action_0
      } = t3;
      await queryClient.cancelQueries({
        queryKey: getDropCurationsQueryKey(dropId)
      });
      const previousCurations = queryClient.getQueryData(getDropCurationsQueryKey(dropId));
      queryClient.setQueryData(getDropCurationsQueryKey(dropId), current => current?.map(curation => curation.id === curationId_0 ? {
        ...curation,
        drop_included: action_0 === "add"
      } : curation) ?? current);
      return {
        previousCurations
      };
    };
    $[2] = dropId;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== invalidateDrops || $[6] !== setToast) {
    t3 = (_data, variables) => {
      if (!variables.options?.suppressToast) {
        setToast({
          type: "success",
          message: variables.action === "add" ? "Drop added to curation." : "Drop removed from curation."
        });
      }
      invalidateDrops();
    };
    $[5] = invalidateDrops;
    $[6] = setToast;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== dropId || $[9] !== queryClient || $[10] !== setToast) {
    t4 = (error, variables_0, context) => {
      if (context?.previousCurations !== undefined) {
        queryClient.setQueryData(getDropCurationsQueryKey(dropId), context.previousCurations);
      }
      if (!variables_0.options?.suppressToast) {
        setToast({
          type: "error",
          message: getCurationMembershipErrorMessage(error, variables_0.action)
        });
      }
    };
    $[8] = dropId;
    $[9] = queryClient;
    $[10] = setToast;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== dropId || $[13] !== queryClient) {
    t5 = async () => {
      await queryClient.invalidateQueries({
        queryKey: getDropCurationsQueryKey(dropId)
      });
    };
    $[12] = dropId;
    $[13] = queryClient;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = {
      mutationFn: t1,
      onMutate: t2,
      onSuccess: t3,
      onError: t4,
      onSettled: t5
    };
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const mutation = useMutation(t6);
  let t7;
  let t8;
  if ($[21] !== mutation) {
    t7 = (curationId_1, action_1, options) => mutation.mutate({
      curationId: curationId_1,
      action: action_1,
      options
    });
    t8 = (curationId_2, action_2, options_0) => mutation.mutateAsync({
      curationId: curationId_2,
      action: action_2,
      options: options_0
    });
    $[21] = mutation;
    $[22] = t7;
    $[23] = t8;
  } else {
    t7 = $[22];
    t8 = $[23];
  }
  const t9 = mutation.isPending ? mutation.variables.curationId : null;
  let t10;
  if ($[24] !== mutation.isPending || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = {
      updateMembership: t7,
      updateMembershipAsync: t8,
      isPending: mutation.isPending,
      pendingCurationId: t9
    };
    $[24] = mutation.isPending;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}